import {
  ButtonProps,
  ButtonVariant,
  Divider,
  Loader,
  Button as MButton,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import classNames from 'classnames';
import Image from 'next/image';
import { PropsWithChildren } from 'react';
import { DropDownMenuItem, GenericMenu } from './Menu';

export const Button = ({
  text,
  prefixIcon,
  suffixIcon,
  active,
  variant,
  theme,
  onClick,
  navigation,
  className,
  slim,
  loading = false,
  disabled = false,
}: {
  text: string;
  prefixIcon?: string | JSX.Element;
  suffixIcon?: string | JSX.Element;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  active?: boolean;
  variant:
    | 'primary'
    | 'secondary'
    | 'outlined'
    | 'disabled'
    | 'link'
    | 'locked';
  theme?: string;
  onClick?: () => void;
  navigation?: boolean;
  slim?: boolean;
}) => {
  const base = `w-full flex justify-center gap-2 px-4 ${slim ? 'py-1' : 'py-3'} rounded-[100px]`;
  const styles = {
    primary: `${base} ${active && 'border-t-0 border-r-0 border-b border-l-0 border-primary-500'} bg-gradient-to-t from-primary-400 from-1.06% via-primary-500 via-50% to-primary-300 to-100%  ${!navigation && 'border border-primary-600'}  ${theme === 'light' ? 'border-primary-600 border	' : ''} ${className}`,
    secondary: `${base} bg-white/75 ${active && 'border-t-0 border-r-0 border-b border-l-0 border-primary-500'}  ${!navigation && 'border border-primary-600'}  ${theme === 'light' ? 'border-primary-600 border	' : ''} ${className}`,
    outlined: `${base} bg-transparent border border-gray-700  ${theme === 'light' ? 'border-secondary-600 border	' : ''} ${className}`,
    link: `${base} bg-transparent ${theme === 'light' ? 'border-secondary-600 border	' : ''} ${className}`,
    disabled: `cursor-not-allowed ${base} !bg-gray-300 ${!navigation && 'border border-primary-600'}  ${theme === 'light' ? 'border-primary-600 border	' : ''} ${className}`,
    locked: `cursor-not-allowed ${base} !bg-gray-300 ${!navigation && 'border border-primary-600'} text-white ${theme === 'light' ? 'border-primary-600 border	' : ''} ${className}`,
  };
  variant = disabled ? 'disabled' : variant;
  const getPrefixIcon = () => {
    if (prefixIcon) {
      if (typeof prefixIcon === 'string') {
        return (
          <Image
            style={{ visibility: !loading ? 'visible' : 'hidden' }}
            src={prefixIcon}
            alt={text}
            width={24}
            height={24}
            priority
          />
        );
      }

      return (
        <span style={{ visibility: !loading ? 'visible' : 'hidden' }}>
          {prefixIcon}
        </span>
      );
    }
  };

  const getSuffixIcon = () => {
    if (suffixIcon) {
      if (typeof suffixIcon === 'string') {
        return (
          <Image
            style={{ visibility: !loading ? 'visible' : 'hidden' }}
            src={suffixIcon}
            alt={text}
            width={24}
            height={24}
            priority
          />
        );
      }

      return (
        <span style={{ visibility: !loading ? 'visible' : 'hidden' }}>
          {suffixIcon}
        </span>
      );
    }
  };

  return (
    <div>
      <button
        disabled={['disabled', 'locked'].includes(variant) || loading || disabled}
        onClick={onClick}
        className={`${styles[variant]} ${loading ? 'cursor-wait' : 'cursor-pointer'}`}
      >
        <div
          className={`text-base ${!navigation && 'font-bold'} ${['secondary', 'link'].includes(variant) && (navigation ? 'text-primary-800' : 'text-primary-600')}  ${(variant === 'primary' || variant === 'disabled') && 'text-white'} ${variant === 'outlined' && 'text-text-secondary'}`}
        >
          <div style={{ display: 'grid', gridTemplateAreas: 'zed' }}>
            <div
              className="flex items-center justify-center "
              style={{
                gridArea: 'zed',
                visibility: loading ? 'visible' : 'hidden',
              }}
            >
              <Loader
                size="xs"
                color={variant === 'secondary' ? '#5fbad3' : 'white'}
              />
            </div>
            <div
              style={{
                gridArea: 'zed',
                visibility: !loading ? 'visible' : 'hidden',
              }}
              className="flex gap-2"
            >
              {getPrefixIcon()}
              {text}
              {getSuffixIcon()}
            </div>
          </div>
        </div>
      </button>
    </div>
  );
};

type Scheme = 'blue' | 'coral' | 'gray';

const SCHEME_TO_COLOR: Record<Scheme, string> = {
  blue: '#58b7d1',
  coral: '#d8725a',
  gray: '#d0d5de',
};

type Variant = 'primary' | 'secondary' | 'outlined' | 'link';

const VARIANT_MAP: Record<Variant, ButtonVariant> = {
  primary: 'filled',
  secondary: 'outline',
  outlined: 'default',
  link: 'subtle',
};

interface ButtonV2Props extends ButtonProps, PropsWithChildren {
  scheme?: Scheme;
  rounded?: boolean;
  slim?: boolean;
  variant: Variant;
  xPadding?: boolean;
  onClick: () => void | Promise<void>;
}

export const ButtonV2 = ({
  children,
  variant,
  scheme = 'blue',
  rounded = true,
  slim = false,
  xPadding = true,
  ...props
}: ButtonV2Props) => {
  const base = classNames('text-base', {
    'rounded-[6.25rem]': rounded,
    'h-[2.75rem]': slim,
    'h-[3rem]': !slim,
    'px-4': xPadding,
    '!px-0': !xPadding,
  });

  const styles: Record<Scheme, Record<Variant, string>> = {
    blue: {
      primary: `${base} bg-gradient-to-t from-primary-400 from-1.06% via-primary-500 via-50% to-primary-300 to-100% border border-primary-600 border-primary-600 border`,
      secondary: `${base} bg-white/75 border border-primary-600 border-primary-600 border`,
      outlined: `${base} bg-transparent border border-gray-700  border-secondary-600 border	`,
      link: `${base} bg-transparent border-secondary-600 border	`,
    },
    coral: {
      primary: `${base} text-white`,
      secondary: `${base} text-[#d8725a] bg-white/75 border border-[#d8725a]`,
      outlined: `${base} bg-transparent text-[#d8725a] border border-[#d8725a]`,
      link: `${base} bg-transparent border-none text-[#d8725a]`,
    },
    gray: {
      primary: `${base} bg-[#d0d5dd] text-white`,
      secondary: `${base} text-[#d0d5dd] bg-white/75 border border-[#d0d5dd]`,
      outlined: `${base} bg-transparent text-[#d0d5dd] border border-[#d0d5dd]`,
      link: `${base} bg-transparent border-none text-[#d0d5dd]`,
    },
  };

  return (
    <MButton
      {...props}
      variant={VARIANT_MAP[variant]}
      color={SCHEME_TO_COLOR[scheme]}
      classNames={{
        root: styles[scheme][variant],
      }}
    >
      {children}
    </MButton>
  );
};

interface ButtonWithMenuProps extends PropsWithChildren {
  scheme?: Scheme;
  rounded?: boolean;
  slim?: boolean;
  variant: Variant;
  xPadding?: boolean;
  onClick: () => void | Promise<void>;
  items: DropDownMenuItem[];
}

export const ButtonWithMenu = ({
  children,
  items,
  variant,
  scheme = 'blue',
  rounded = true,
  slim = false,
  xPadding = true,
  onClick,
}: ButtonWithMenuProps) => {
  const base = classNames('text-base', {
    'rounded-[6.25rem]': rounded,
    'h-[2.75rem]': slim,
    'h-[3rem]': !slim,
    'px-4': xPadding,
    '!px-0': !xPadding,
  });

  const styles: Record<Scheme, Record<Variant, string>> = {
    blue: {
      primary: `${base} bg-gradient-to-t from-primary-400 from-1.06% via-primary-500 via-50% to-primary-300 to-100% border border-primary-600 border-primary-600 border`,
      secondary: `${base} bg-white/75 border border-primary-600 border-primary-600 border`,
      outlined: `${base} bg-transparent border border-gray-700  border-secondary-600 border	`,
      link: `${base} bg-transparent border-secondary-600 border	`,
    },
    coral: {
      primary: `${base} text-white`,
      secondary: `${base} text-[#d8725a] bg-white/75 border border-[#d8725a]`,
      outlined: `${base} bg-transparent text-[#d8725a] border border-[#d8725a]`,
      link: `${base} bg-transparent border-none text-[#d8725a]`,
    },
    gray: {
      primary: `${base} bg-[#d0d5dd] text-white`,
      secondary: `${base} text-[#d0d5dd] bg-white/75 border border-[#d0d5dd]`,
      outlined: `${base} bg-transparent text-[#d0d5dd] border border-[#d0d5dd]`,
      link: `${base} bg-transparent border-none text-[#d0d5dd]`,
    },
  };

  return (
    <div
      className={`flex items-center ${styles[scheme][variant]} !border-l-0 !pl-0  py-4`}
    >
      <div>
        <MButton
          onClick={onClick}
          variant={VARIANT_MAP[variant]}
          color={SCHEME_TO_COLOR[scheme]}
          classNames={{
            root: classNames(styles[scheme][variant], {
              '!rounded-l-[6.25rem] rounded-r-none border-r-0': rounded,
            }),
          }}
        >
          {children}
        </MButton>
      </div>
      <div>
        <Divider
          size="xs"
          color="white"
          className="h-[30px]"
          orientation="vertical"
        />
      </div>
      <GenericMenu
        component={
          <div className="pl-2">
            <IconChevronDown color="white" />
          </div>
        }
        items={items}
      />
    </div>
  );
};
